import CloseRounded from "@mui/icons-material/CloseRounded";
import DownloadOutlined from "@mui/icons-material/DownloadOutlined";
import OpenInNewOutlined from "@mui/icons-material/OpenInNewOutlined";
import VisibilityOutlined from "@mui/icons-material/VisibilityOutlined";
import Button from "@mui/material/Button";
import IconButton from "@mui/material/IconButton";
import Tooltip from "@mui/material/Tooltip";
import { useCallback, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import Flx from "../../../../../components/layout/Flx";
import { useLoanFilesHook } from "../../../../../hooks/useLoanFilesHook";
import { loanDrilldownSet } from "../../../../../redux/actions/loanDrilldownActions";
import { sidebarSetValues } from "../../../../../redux/actions/sidebarActions";
import LoanFileCommentsBadge from "../../../../../components/common/Loan/LoanFileCommentsBadge";
import { Box } from "@mui/material";

const CellRendererLoanFileActions = ({
  params,
  isEditing,
  onStopEdit,
  originalData,
}) => {
  const { loading, getFileById, updateLoanDocMetadata } = useLoanFilesHook();
  const [externalDownloadLoading, setExternalDownloadLoading] = useState(null);
  const [saveLoading, setSaveLoading] = useState(false);
  const loanDrilldown = useSelector((state) => state.loanDrilldown);
  const dispatch = useDispatch();

  const onPreviewClick = useCallback(
    (file) => {
      dispatch(
        sidebarSetValues({
          type: "filePreview",
          state: file,
        }),
      );
    },
    [dispatch],
  );

  const onDownloadClick = () => {
    getFileById({
      loanId: params?.data?.dealId,
      file_storage_name: params?.data?.file_storage_name,
      fileDownloadName: params?.data?.file_display_name,
      fileDownload: true,
    });
  };

  const handleOnExternalDownloadClick = useCallback(
    (params) => {
      setExternalDownloadLoading(true);
      getFileById({
        loanId: params?.data?.dealId,
        file_storage_name: params?.data?.file_storage_name,
        fileDownloadName: params?.data?.file_display_name,
        openInNewTab: true,
        onSuccessFn: () => {
          setExternalDownloadLoading(false);
        },
      });
    },
    [getFileById],
  );

  const handleOnPreviewClick = useCallback(
    (params) => {
      onPreviewClick(params?.data);
    },
    [onPreviewClick],
  );

  const getChangedFields = useCallback((currentData, originalData) => {
    const changes = { _id: currentData._id };

    Object.keys(currentData).forEach((key) => {
      if (currentData[key] !== originalData[key]) {
        changes[key] = currentData[key];
      }
    });

    // Remove _id from comparison but keep it in the result
    if (Object.keys(changes).length === 1) {
      return null; // No changes
    }

    return changes;
  }, []);

  const handleSaveChanges = useCallback(() => {
    if (!originalData) return;

    const changedFields = getChangedFields(params.data, originalData);

    if (!changedFields) {
      onStopEdit();
      return;
    }

    setSaveLoading(true);

    updateLoanDocMetadata({
      loanId: loanDrilldown?._id,
      data: [changedFields],
      onSuccessFn: (data) => {
        dispatch(loanDrilldownSet(data));
        onStopEdit();
        setSaveLoading(false);
      },
      onErrorFn: () => {
        setSaveLoading(false);
      },
    });
  }, [
    params.data,
    originalData,
    getChangedFields,
    onStopEdit,
    updateLoanDocMetadata,
    loanDrilldown?._id,
    dispatch,
  ]);

  if (isEditing) {
    return (
      <Flx
        fw
        jc
        sx={{
          width: "190px",
          ml: "-5px",
          ".MuiIconButton-root": {
            borderRadius: "0",
            width: "32px",
            height: "30px",
            p: 0,
            pt: 0.3,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
          },
        }}
      >
        <Tooltip title="Cancel">
          <IconButton
            onClick={onStopEdit}
            size="small"
            color="error"
            disabled={saveLoading}
          >
            <CloseRounded className="thin-5" sx={{ fontSize: 18 }} />
          </IconButton>
        </Tooltip>

        <Button
          onClick={handleSaveChanges}
          size="small"
          color="primary"
          loading={saveLoading}
          disabled={saveLoading}
        >
          Save Changes
        </Button>
      </Flx>
    );
  }

  return (
    <Flx
      ac
      sx={{
        ".MuiButtonBase-root": {
          borderRadius: "0",
          width: "32px",
          height: "30px",
          p: 0,
          pt: 0.3,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
        },
      }}
    >
      <Tooltip title="Download">
        <IconButton
          disabled={loading && !externalDownloadLoading}
          onClick={onDownloadClick}
          size="small"
          color="primary"
        >
          <DownloadOutlined className="thin-7" sx={{ fontSize: 20 }} />
        </IconButton>
      </Tooltip>
      <Tooltip title="Open In New Tab">
        <IconButton
          disabled={externalDownloadLoading}
          onClick={() => handleOnExternalDownloadClick(params)}
          size="small"
          color="primary"
        >
          <OpenInNewOutlined className="thin-6" sx={{ fontSize: 16 }} />
        </IconButton>
      </Tooltip>
      <Tooltip title="Preview File In Sidebar">
        <IconButton
          onClick={() => handleOnPreviewClick(params)}
          size="small"
          color="primary"
        >
          <VisibilityOutlined className="thin-5" sx={{ fontSize: 18 }} />
        </IconButton>
      </Tooltip>
      <Tooltip title="Add Comment To File">
        <Box>
          <LoanFileCommentsBadge
            color={"primary"}
            showZero
            file={params.data}
          />
        </Box>
      </Tooltip>
    </Flx>
  );
};

export default CellRendererLoanFileActions;
